'use client'
import { type FC, memo, useEffect, useMemo, useState } from 'react'

import { useFilterProductsQuery } from '../api/catalogApi'

import { ProductList } from './ProductList'
import type { CatalogWrapperProps, TCatalogComponents } from '../model/catalogTypes'
import { TProduct } from '../config'
import type { TMainData } from '../model/productListTypes'
import { useAppSelector } from '@/redux/hooks'
import { Filter } from './Filter'
import { Checkbox } from './Checkbox'
import { useParams } from 'next/navigation'
import { type TypeOfAnimal } from '@/redux/services/catalogApi'
import { FilterWrapper } from '@/screens/catalog/ui/components/FilterWrapper'
import { useMediaQuery } from 'usehooks-ts'

export interface ICatalogProps extends TCatalogComponents, Omit<TMainData, 'setPage' | 'data'> {
    // , TNotFoundWithComponent
    CatalogWrapper: FC<CatalogWrapperProps>
    Loader?: FC<Partial<HTMLDivElement>>
    defaultData?: { count: number; data: TProduct[] }
    catalogParams: string[] | null
    filters: any
    categories: TypeOfAnimal[]
    searchValue: string
    setSearchValue: any,
    sort: any,
    setSort: any
}
const FilterOptionWrapper = ({ children }) => <>{children}</>

export const Catalog = memo(
    ({
        CatalogWrapper,
        ListWrapper,
        notFoundText,
        ItemCard,
        Pagination,
        LoadMoreButton,
        Loader,
        defaultData,
        NotFound,
        filters,
        catalogParams,
        categories,
        searchValue,
        setSearchValue,
        sort,
        setSort
    }: ICatalogProps) => {
        const [page, setPage] = useState<number>(0)
        const [countPerPage, setCountPage] = useState<number>(10)
        const [products, setProducts] = useState([])
        const isMobile = useMediaQuery('(max-width: 1250px)')

        const { editableFilter } = useAppSelector(state => state.catalog)
        const { subCategory } = useAppSelector(state => state.catalogv1)

        const params = useParams()

        const animalType = useMemo(() => {
            const category = params.catalog?.[0]

            if (!category || !categories) {
                return null
            }

            const animalType = categories.find(c => c.translit === category)

            if (animalType) {
                if (subCategory) {
                    return `&animal_type=${animalType.id}&animal_type_category=${subCategory.id}`
                } else return `&animal_type=${animalType.id}`
            }
        }, [params, categories, subCategory])

        const { data: server_data, isFetching } = useFilterProductsQuery({
            page: page,
            count: countPerPage,
            filter: editableFilter,
            ...(sort && {sort: sort}),
            animalType,
            searchValue
        })

        useEffect(() => {
            setPage(0)
        }, [searchValue, animalType])

        useEffect(() => {
            if (!server_data) return

            if (page > 0) {
                setProducts(prevProducts => Array.from(new Set([...prevProducts, ...server_data.data])))
            } else {
                setProducts(server_data.data)
            }
        }, [server_data, page, setProducts])

        return (
            <CatalogWrapper>
                {!isFetching ? (
                    <ProductList
                        ListWrapper={ListWrapper}
                        data={products ? products : []}
                        notFoundText={notFoundText}
                        ItemCard={ItemCard}
                        NotFound={NotFound}
                        Pagination={Pagination}
                        count={server_data?.count}
                        countPerPage={countPerPage}
                        setCountPage={setCountPage}
                        LoadMoreButton={LoadMoreButton}
                        page={page}
                        setPage={setPage}
                        setSearchValue={setSearchValue}
                    />
                ) : defaultData ? (
                    <ProductList
                        setSearchValue={setSearchValue}
                        ListWrapper={ListWrapper}
                        data={defaultData && defaultData.data ? defaultData.data : []}
                        notFoundText={notFoundText}
                        ItemCard={ItemCard}
                        NotFound={NotFound}
                        Pagination={Pagination}
                        count={defaultData?.count}
                        countPerPage={countPerPage}
                        setCountPage={setCountPage}
                        LoadMoreButton={LoadMoreButton}
                        page={page}
                        setPage={setPage}
                    />
                ) : Loader ? (
                    <Loader />
                ) : null}

                {!isMobile ? (
                    <Filter
                        components={{
                            FilterWrapper: FilterWrapper,
                            FilterOptionWrapper: FilterOptionWrapper,
                            FilterCheckbox: Checkbox
                        }}
                        catalogParams={catalogParams}
                        filterConfig={(optionTitle: string) => {
                            switch (optionTitle) {
                                case 'Тип продукта':
                                    return { type: 'checkbox', updateUrl: 'replace' }
                                default:
                                    return { type: 'checkbox' }
                            }
                        }}
                        filterData={filters ? filters : []}
                    />
                ) : null}
            </CatalogWrapper>
        )
    }
)
